import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Image } from './wrappers/Image';
import CloseIcon from '~/components/icons/ArrowRight';
import { Typography } from './typography/Typography';
import { Flex } from './wrappers/Flex';
import SizeTable from '~/sections/sizing/SizeTable';
import gsap from 'gsap';

import { ProductSizing } from '~/common/constants/dictionary';

interface ISizeGuideDrawer {
  toggleSizingModal: () => void;
  productSizing?: ProductSizing;
  isSizingOpen: boolean;
}

const SizeGuideDrawer = ({
  toggleSizingModal,
  productSizing,
  isSizingOpen,
}: ISizeGuideDrawer) => {
  const sizeModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSizingOpen) {
      gsap.to(sizeModalRef.current, {
        right: 0,
        ease: 'sin.inout',
        duration: 0.3,
      });
    } else {
      gsap.to(sizeModalRef.current, {
        right: '-100%',
        ease: 'sin.inout',
        duration: 0.5,
      });
    }
  }, [isSizingOpen]);

  return (
    <SizingGuideWrapper ref={sizeModalRef}>
      <ButtonWrapping>
        <StyledCloseIcon onClick={toggleSizingModal} />
      </ButtonWrapping>
      <Guide>
        <MainHeading variant="h2">Size guide</MainHeading>
        <TypeHeading>{productSizing?.title}</TypeHeading>
        <ImageWrapper
          height={productSizing?.imgStyle?.height}
          my={productSizing?.imgStyle?.my}
        >
          <StyledImage src={productSizing?.img as string} />
        </ImageWrapper>
        <SizeTable data={productSizing}></SizeTable>
      </Guide>
    </SizingGuideWrapper>
  );
};

export default SizeGuideDrawer;

export const TypeHeading = styled.p`
  font-size: 1rem;
  text-transform: uppercase;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};

  @media ${({ theme }) => theme.breakpoints.sm} {
    font-size: 0.75rem;
  }
`;

export const ImageWrapper = styled.div<{ height?: string; my?: string }>`
  height: ${({ height }) => (height ? `${height}px` : '22.875rem')};
  margin: ${({ my }) => `${my}px 0` || '2rem 0'};

  @media ${({ theme }) => theme.breakpoints.sm} {
    height: ${({ height }) => height || '21.4375rem'};
    margin: ${({ my }) => `${Number(my) - 16}px 0` || '1rem 0'};
  }
`;

export const StyledImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const SizingGuideWrapper = styled.div`
  * {
    color: black !important;
  }
  position: fixed;
  padding: 2rem 0;
  width: 73.38%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  z-index: ${({ theme }) => theme.zIndex.modal};
  right: -100%;
  top: 0;
  overflow: auto;
  transition: all 0.5s ease-out;

  @media ${({ theme }) => theme.breakpoints.md} {
    width: 100%;
    padding: 3rem 0;
  }
  @media ${({ theme }) => theme.breakpoints.sm} {
    transition: all 0.3s ease-out;
    padding: 1.5rem 0;
  }
`;

export const ButtonWrapping = styled.div`
  padding: 0 3.725rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 0 0.5rem;
  }
`;

export const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
  path {
    fill: ${({ theme }) => theme.colors.primaryDark};
    transition: fill 0.2s ease-in;
  }
  &:hover path {
    fill: ${({ theme }) => theme.colors.textHover};
  }
`;

export const MainHeading = styled(Typography)`
  font-size: 36px;
  margin-top: 4.875rem;
  margin-bottom: 1.5rem;

  @media ${({ theme }) => theme.breakpoints.md} {
    margin-top: 4.375rem;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    margin-top: 1rem;
    font-size: 1.5rem;
  }
`;

export const Guide = styled(Flex)`
  margin: 0 4.625rem;
  flex-direction: column;
  align-items: center;

  @media ${({ theme }) => theme.breakpoints.md} {
    margin-right: 0;
  }
  @media ${({ theme }) => theme.breakpoints.sm} {
    margin: 0;
  }
`;
